import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  type ApiError,
  api,
  type ListContactsResult,
} from '@whinerp/client';
import {
  CapabilityUnavailable,
  DataTable,
  EmptyState,
  ErrorState,
  ListChrome,
  Num,
  PaginationFooter,
  Skeleton,
  type Column,
  type RowDensity,
} from '@whinerp/ui';
import { absent, formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';
import { NewContact } from './new-contact.js';
import { ViewBar } from '../shared/view-bar.js';
import { nextSort, useSavedView, visibleColumns } from '../shared/use-saved-view.js';
import { defineScreen } from '../types.js';

/**
 * SC-06 Contacts.
 *
 * The full control set, because density is capability. What is simplified in
 * this product is the model underneath - three sharing concepts, one
 * permission-set concept - never the controls a rep touches every day.
 *
 * The next step is a first-class column. "Nothing scheduled" renders as a
 * state rather than an empty cell, because a blank cell reads as missing data
 * and this is the gap the whole next-step model asks somebody to close.
 */

type Row = ListContactsResult['rows'][number];

function ContactsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { session } = useSession();
  const navigate = useNavigate();
  const contacts = useEntitlement('F-027');

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });
  const [density, setDensity] = useState<RowDensity>('comfortable');
  const [inlineEdit, setInlineEdit] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  /*
   * `?new=1` opens the form (G-021), so the command palette can reach it
   * without the mouse. Read once as the starting state, and the parameter is
   * dropped on close: leaving it in the URL would reopen the form on every
   * refresh and on the back button.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(() => searchParams.get('new') === '1');
  const closeCreating = (): void => {
    setCreating(false);
    if (searchParams.has('new')) {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  };

  // The saved view, its unsaved draft and what a request has to send: one
  // answer, shared with every other list.
  const savedView = useSavedView(() => setCursorStack([]));

  const cursor = cursorStack[cursorStack.length - 1] ?? null;

  const query = useQuery<ListContactsResult, ApiError>({
    queryKey: ['contacts', { search, sort, cursor, ownerFilter, view: savedView.params }],
    queryFn: () =>
      api.listContacts({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
          ...(ownerFilter ? { ownerId: ownerFilter } : {}),
          ...savedView.params,
          sort: sort.field,
          direction: sort.direction,
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('object.contact'),
        sortField: 'name',
        // The column the row IS. Simple never drops it.
        essential: true,
        cell: (row) => row.fullName || t('state.notSet'),
      },
      { key: 'title', header: 'Title', sortField: 'title', cell: (row) => row.title ?? '' },
      {
        key: 'company',
        header: t('object.account'),
        sortField: 'company',
        cell: (row) => row.accountName ?? '',
      },
      {
        key: 'email',
        header: 'Email',
        cell: (row) => row.primaryEmail ?? '',
        hideOnCard: true,
      },
      {
        key: 'nextStep',
        header: t('object.nextStep'),
        /*
         * Kept in Simple. This column is F-027's promise: a contact with
         * nothing scheduled SAYS so, because that gap is what the list exists
         * to make somebody close. Budgeting it away leaves a tidier list that
         * no longer does its job.
         */
        essential: true,
        cell: (row) =>
          row.nextStepDue && row.nextStepType ? (
            <span className="flex items-center gap-2">
              <span>{row.nextStepType}</span>
              <Num className="text-ink-meta">{formatRelativeDate(row.nextStepDue, format)}</Num>
            </span>
          ) : (
            // A state, not a blank. This is the gap the model asks a rep to close.
            <span className="text-ink-300">{absent('nothing-scheduled', format).text}</span>
          ),
      },
      { key: 'owner', header: 'Owner', sortField: 'owner', cell: (row) => row.ownerName ?? '', hideOnCard: true },
      { key: 'phone', header: 'Phone', cell: (row) => row.primaryPhone ?? '', hideOnCard: true },
    ],
    [t, format],
  );

  /*
   * The columns the server says to show, in the order it says (F-021).
   *
   * The server decides rather than the screen, because it is the server that
   * knows what the saved view holds and which of its keys this object still
   * has a column for. A screen that filtered by `draft.columns` itself would
   * be a second answer to the same question, and the two would disagree the
   * first time somebody opened a view saved before a column was renamed.
   */
  const visible = useMemo(
    () => visibleColumns(columns, query.data?.columns ?? null),
    [columns, query.data?.columns],
  );

  /*
   * What a group heading says, per groupable field.
   *
   * Written out rather than indexed off the row, because the API field name
   * and the row property are deliberately different - `company` is
   * `accountName` - and a lookup by string would be a fourth place the two
   * vocabularies have to agree.
   */
  const groupLabel = useMemo<Record<string, (row: Row) => string | null>>(
    () => ({
      company: (row) => row.accountName,
      owner: (row) => row.ownerName,
      title: (row) => row.title,
    }),
    [],
  );
  const groupBy = query.data?.groupBy ?? null;
  const grouping = groupBy ? groupLabel[groupBy] : undefined;

  /*
   * The groups the SERVER says it will honour, labelled here.
   *
   * The server decides what works and the screen decides what it is called: a
   * list written on the screen alone offered "status" on a queue that could
   * not order by it, so choosing it changed nothing and said nothing.
   */
  const groupNames: Record<string, string> = {
    company: t('object.account'),
    owner: 'Owner',
    title: 'Title',
  };
  const groupable = (query.data?.groupable ?? [])
    .filter((field) => Object.hasOwn(groupNames, field))
    .map((field) => ({ field, label: groupNames[field]! }));

  if (contacts.state === 'plan-locked') {
    return (
      <CapabilityUnavailable
        kind="plan-locked"
        capability={t('object.contact.plural')}
        title={t('capability.planLocked.title')}
        body={t('capability.planLocked.body', {
          values: {
            plan: contacts.entry?.planMinLabel ?? '',
            current: session.workspace.planLabel,
          },
        })}
        action={{ label: t('capability.planLocked.action', { values: { plan: contacts.entry?.planMinLabel ?? '' } }), href: '/setup/plan' }}
      />
    );
  }

  const rows = query.data?.rows ?? [];
  const filtered = search.trim().length > 0 || ownerFilter !== null;

  return (
    <div className="flex w-full flex-col gap-3">
      {/* The call queue works through this list, so it is opened from here. */}
      <Link to="/call-queue" data-open-call-queue className="self-start text-dense text-bronze underline">
        {t('callQueue.title')}
      </Link>
    <ListChrome
      labels={{
        searchThisList: t('list.searchThisList'),
        new: t('action.new'),
        import: t('action.import'),
        moreActions: t('list.moreActions'),
        refresh: t('action.refresh'),
        listSettings: t('list.settings'),
        filters: t('list.filters'),
        removeFilter: (filter) => t('list.removeFilter', { values: { filter } }),
        densityComfortable: t('list.density.comfortable'),
        densityCompact: t('list.density.compact'),
        showAsChart: t('list.chartView'),
        showAsTable: t('list.tableView'),
        inlineEdit: t('list.inlineEdit'),
        unsavedChanges: t('list.view.unsaved'),
        save: t('action.save'),
        saveAs: t('action.saveAs'),
        discard: t('action.discard'),
        clearSelection: t('list.clearSelection'),
        bulkToolbar: t('list.bulkToolbar'),
        pinView: (name) => t('list.view.pin') + ': ' + name,
        unpinView: (name) => t('list.view.unpin') + ': ' + name,
      }}
      icon="Users"
      title={t('object.contact.plural')}
      // No `views` here: the view control is the ViewBar above the table. A
      // second selector offering one option called "All contacts", with an
      // onSelect that did nothing, was a control that could not change
      // anything sitting above one that could.
      unsaved={null}
      total={query.data?.total ?? 0}
      totalLabel={t('list.status.records', { values: { count: formatCount(query.data?.total ?? 0, format) } })}
      sortLabel={t('list.status.sortedBy', { values: { field: sort.field } })}
      filterLabel={filtered ? t('list.status.filteredBy', { values: { filters: 'search' } }) : null}
      updatedLabel={t('list.status.updated', {
        values: { when: query.dataUpdatedAt ? formatRelativeDate(new Date(query.dataUpdatedAt), format) : '—' },
      })}
      // Was () => undefined: the toolbar's primary action, on every contact
      // list, doing nothing at all.
      onNew={() => setCreating(true)}
      // Import goes to the import screen, which exists. It was () => undefined.
      onImport={() => navigate('/setup/import')}
      objectActions={[]}
      /*
       * No Export and no Recycle bin.
       *
       * Both were menu items with `() => undefined` behind them. There is no
       * record-list export operation and no way to LIST deleted records -
       * `restoreRecord` exists but nothing enumerates what could be restored -
       * so both were names for things that do not exist. They come back with
       * the features, not before them.
       */
      overflowActions={[]}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        setCursorStack([]);
      }}
      density={density}
      onDensityChange={setDensity}
      onRefresh={() => void query.refetch()}
      refreshing={query.isFetching}
      inlineEdit={inlineEdit}
      onInlineEditChange={setInlineEdit}
      view={view}
      onViewChange={setView}
      filterChips={
        ownerFilter
          ? [{ key: 'owner', label: 'My contacts', onRemove: () => setOwnerFilter(null) }]
          : []
      }
      selectedCount={selected.size}
      selectedLabel={t('list.selected', { values: { count: formatCount(selected.size, format) } })}
      onClearSelection={() => setSelected(new Set())}
      bulkActions={[
        { key: 'assign', label: 'Change owner', icon: 'UserGear', onSelect: () => undefined },
        { key: 'tag', label: 'Add tag', icon: 'Tag', onSelect: () => undefined },
        { key: 'delete', label: `Delete ${String(selected.size)} contacts`, icon: 'Trash', onSelect: () => undefined },
      ]}
      footer={
        <PaginationFooter
          showingLabel={t('list.page.showing', {
            values: {
              from: formatCount(cursorStack.length * 50 + 1, format),
              to: formatCount(cursorStack.length * 50 + rows.length, format),
            },
          })}
          previousLabel={t('list.page.previous')}
          nextLabel={t('list.page.next')}
          navigationLabel={t('list.page.next')}
          hasPrevious={cursorStack.length > 0}
          hasNext={query.data?.hasMore ?? false}
          onPrevious={() => setCursorStack((stack) => stack.slice(0, -1))}
          onNext={() => {
            const next = query.data?.nextCursor;
            if (next) setCursorStack((stack) => [...stack, next]);
          }}
        />
      }
    >
      <ViewBar
        objectKey="contact"
        active={savedView.active}
        draft={savedView.draft}
        columns={columns.map((column) => ({ key: column.key, label: column.header }))}
        groupable={groupable}
        onChange={savedView.onChange}
        onSelect={savedView.onSelect}
        onRemoveFilter={savedView.onRemoveFilter}
        onDiscard={savedView.onDiscard}
      />

      {/* The form the New button opens, above the list it adds to. */}
      {creating ? <NewContact onDone={closeCreating} /> : null}

      {query.isError ? (
        <ErrorState
          title={t('state.error')}
          body={query.error.message}
          {...(query.error.rule ? { rule: query.error.rule } : {})}
          onRetry={() => void query.refetch()}
          retryLabel={t('action.retry')}
        />
      ) : (
        <DataTable<Row>
          mode={session.preferences.density}
          label={t('object.contact.plural')}
          labels={{
            sortBy: (column) => t('list.sortBy', { values: { column } }),
            selectRow: (row) => t('list.selectRow', { values: { row } }),
            rowsShown: (count) => t('list.rowsShown', { values: { count: formatCount(count, format) } }),
          }}
          rows={rows}
          columns={visible}
          {...(grouping ? { group: { label: grouping, emptyLabel: t('view.group.empty') } } : {})}
          rowId={(row) => row.id}
          rowHref={(row) => `/contacts/${row.id}`}
          density={density}
          sort={sort}
          onSortChange={(field) => {
            setSort((current) => nextSort(current, field));
            setCursorStack([]);
          }}
          selection={{
            selected,
            onToggle: (id) =>
              setSelected((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              }),
            onToggleAll: () =>
              setSelected((current) =>
                current.size === rows.length ? new Set() : new Set(rows.map((row) => row.id)),
              ),
            allLabel: t('list.selectAll', { values: { count: formatCount(rows.length, format) } }),
          }}
          loading={query.isPending}
          empty={
            filtered ? (
              // Filtered to nothing is a different state from genuinely empty,
              // and the action it offers is different too.
              <EmptyState
                reason="filtered-to-nothing"
                illustration="MagnifyingGlassMinus"
                title={t('state.filteredToNothing')}
                body={t('state.filteredToNothing.body')}
                primary={{
                  label: t('action.clearFilters'),
                  onClick: () => {
                    setSearch('');
                    setOwnerFilter(null);
                  },
                }}
              />
            ) : (
              <EmptyState
                reason="no-records"
                illustration="Users"
                title="No contacts yet"
                body="Add the people you work with, or bring them in from a spreadsheet."
                primary={{
                  label: t('action.new'),
                  icon: 'Plus',
                  // It was wired to () => undefined: a button that looked like
                  // the way to add a contact and did nothing at all.
                  onClick: () => setCreating(true),
                }}
                secondary={{ label: t('action.import'), icon: 'Upload', onClick: () => undefined }}
                tertiary={{ label: t('action.loadSample'), onClick: () => undefined }}
              />
            )
          }
        />
      )}
    </ListChrome>
    </div>
  );
}

function ContactsLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-3" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">Loading contacts</span>
      <Skeleton className="h-line-h3 w-block-lg" />
      <Skeleton className="h-input w-full" />
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}

function ContactsEmpty(): JSX.Element {
  return (
    <EmptyState
      reason="no-records"
      illustration="Users"
      title="No contacts yet"
      body="Add the people you work with, or bring them in from a spreadsheet."
      primary={{ label: 'Add a contact', icon: 'Plus', href: '/contacts' }}
      secondary={{ label: 'Import from a spreadsheet', icon: 'Upload', href: '/setup/import' }}
    />
  );
}

function ContactsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Contacts could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-06',
  route: '/contacts',
  surface: 'page',
  title: 'Contacts',
  // Only what this screen genuinely serves today. The next-step column reads
  // real next-step data, but F-155 has no operations of its own yet and the
  // view selector shows one system view, so claiming F-021 and F-155 here
  // would report a reachable UI for features that are half built. They are
  // added back when their operations and their controls land.
  features: ['F-027', 'F-014', 'F-017', 'F-021'],
  entitlement: 'F-027',
  reachedBy: [{ kind: 'nav', item: 'contacts' }],
  // What renders with data in the list. The empty state is a declared state
  // rather than a component of the populated screen, and is rendered and
  // checked as one.
  // ViewBar, not ViewSelector. There was one of each: the ViewBar reads and
  // writes saved views, and the ViewSelector above it offered a single option
  // called "All" with an onSelect that did nothing. One control per question.
  components: ['DataTable', 'FilterChips', 'ViewBar'],
  elements: ['ListChrome', 'StatusLine', 'PaginationFooter'],
  states: {
    empty: ContactsEmpty,
    loading: ContactsLoading,
    error: ContactsError,
  },
  component: ContactsListScreen,
});

export default screen;
